/**
 * Integration Account Component
 *
 * A flexible component for displaying integration account information with various
 * display variants. Used for showing connected accounts in settings tables, panels,
 * and status indicators.
 *
 * Features:
 * - Multiple display variants (pill, panel, badge)
 * - Avatar support with fallback to integration icons
 * - Loading states and markdown support
 * - Status indicators for badge variant
 * - Flexible content rendering
 *
 * @example
 * ```html
 * <uo-integration-account
 *     username="john.doe@example.com"
 *     avatar="https://example.com/avatar.jpg"
 *     integration="slack"
 *     variant="panel"
 *     message="Connected via OAuth">
 *     <uo-button>Disconnect</uo-button>
 * </uo-integration-account>
 * ```
 *
 * @since 6.9.0 Initial implementation
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
var _AutomatorIntegrationAccount_username_accessor_storage, _AutomatorIntegrationAccount_avatar_accessor_storage, _AutomatorIntegrationAccount_integration_accessor_storage, _AutomatorIntegrationAccount_variant_accessor_storage, _AutomatorIntegrationAccount_status_accessor_storage, _AutomatorIntegrationAccount_loading_accessor_storage, _AutomatorIntegrationAccount_message_accessor_storage, _AutomatorIntegrationAccount_class_accessor_storage;
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
// Import styles
import styles from './web-component-style.scss?inline';
/**
 * Integration Account Component
 * @element uo-integration-account
 */
let AutomatorIntegrationAccount = class AutomatorIntegrationAccount extends LitElement {
    constructor() {
        super(...arguments);
        _AutomatorIntegrationAccount_username_accessor_storage.set(this, '');
        _AutomatorIntegrationAccount_avatar_accessor_storage.set(this, '');
        _AutomatorIntegrationAccount_integration_accessor_storage.set(this, '');
        _AutomatorIntegrationAccount_variant_accessor_storage.set(this, 'pill');
        _AutomatorIntegrationAccount_status_accessor_storage.set(this, '');
        _AutomatorIntegrationAccount_loading_accessor_storage.set(this, false);
        _AutomatorIntegrationAccount_message_accessor_storage.set(this, '');
        _AutomatorIntegrationAccount_class_accessor_storage.set(this, '');
    }
    /**
     * The username or account name
     */
    get username() { return __classPrivateFieldGet(this, _AutomatorIntegrationAccount_username_accessor_storage, "f"); }
    set username(value) { __classPrivateFieldSet(this, _AutomatorIntegrationAccount_username_accessor_storage, value, "f"); }
    /**
     * The avatar/profile picture URL
     */
    get avatar() { return __classPrivateFieldGet(this, _AutomatorIntegrationAccount_avatar_accessor_storage, "f"); }
    set avatar(value) { __classPrivateFieldSet(this, _AutomatorIntegrationAccount_avatar_accessor_storage, value, "f"); }
    /**
     * The integration icon to display
     */
    get integration() { return __classPrivateFieldGet(this, _AutomatorIntegrationAccount_integration_accessor_storage, "f"); }
    set integration(value) { __classPrivateFieldSet(this, _AutomatorIntegrationAccount_integration_accessor_storage, value, "f"); }
    /**
     * The variant/style of the account display
     */
    get variant() { return __classPrivateFieldGet(this, _AutomatorIntegrationAccount_variant_accessor_storage, "f"); }
    set variant(value) { __classPrivateFieldSet(this, _AutomatorIntegrationAccount_variant_accessor_storage, value, "f"); }
    /**
     * Additional status for badge variant (success, error, warning, info)
     */
    get status() { return __classPrivateFieldGet(this, _AutomatorIntegrationAccount_status_accessor_storage, "f"); }
    set status(value) { __classPrivateFieldSet(this, _AutomatorIntegrationAccount_status_accessor_storage, value, "f"); }
    /**
     * Whether the account is in a loading state
     */
    get loading() { return __classPrivateFieldGet(this, _AutomatorIntegrationAccount_loading_accessor_storage, "f"); }
    set loading(value) { __classPrivateFieldSet(this, _AutomatorIntegrationAccount_loading_accessor_storage, value, "f"); }
    /**
     * Additional message content with markdown support
     */
    get message() { return __classPrivateFieldGet(this, _AutomatorIntegrationAccount_message_accessor_storage, "f"); }
    set message(value) { __classPrivateFieldSet(this, _AutomatorIntegrationAccount_message_accessor_storage, value, "f"); }
    /**
     * Additional classes to apply
     */
    get class() { return __classPrivateFieldGet(this, _AutomatorIntegrationAccount_class_accessor_storage, "f"); }
    set class(value) { __classPrivateFieldSet(this, _AutomatorIntegrationAccount_class_accessor_storage, value, "f"); }
    /**
     * Render the avatar with fallback to integration icon
     *
     * @param {number} size - The size of the avatar/icon
     *
     * @return {TemplateResult} The rendered avatar template
     */
    renderAvatar(size = 24) {
        if (this.avatar) {
            return html `
				<img src="${this.avatar}" alt="${this.username}" width="${size}" height="${size}">
			`;
        }
        return html `
			<uo-icon integration="${this.integration}" width="${size}" height="${size}"></uo-icon>
		`;
    }
    /**
     * Render the username with optional integration icon
     *
     * @param {boolean} showIcon - Whether to show the integration icon
     *
     * @return {TemplateResult} The rendered username template
     */
    renderUsername(showIcon = false) {
        return html `
			<div class="integration-account__username">
				<uo-markdown 
					allowed-tags="a"
					.content=${this.username}>
				</uo-markdown>
				${showIcon && this.integration ? html `
					<uo-icon integration="${this.integration}"></uo-icon>
				` : ''}
			</div>
		`;
    }
    /**
     * Render the message with markdown support
     *
     * @return {TemplateResult} The rendered message template
     */
    renderMessage() {
        if (!this.message) {
            return html ``;
        }
        return html `
			<div class="integration-account__message">
				<uo-markdown 
					allowed-tags="strong,em,a"
					.content=${this.message}>
				</uo-markdown>
			</div>
		`;
    }
    /**
     * Render loading state based on variant
     *
     * @return {TemplateResult} The rendered loading state template
     */
    renderLoadingState() {
        switch (this.variant) {
            case 'panel':
                return html `
					<div class="integration-account integration-account--panel integration-account--loading">
						<div class="integration-account__avatar">
							<uo-placeholder width="48px" height="48px"></uo-placeholder>
						</div>
						<div class="integration-account__content">
							${this.renderUsername(true)}
							${this.message ? html `
								<div class="integration-account__message">
									<uo-placeholder width="200px"></uo-placeholder>
								</div>
							` : ''}
							<slot></slot>
						</div>
					</div>
				`;
            case 'badge':
                return html `
					<div class="integration-account integration-account--badge integration-account--loading">
						<uo-placeholder width="80px"></uo-placeholder>
					</div>
				`;
            case 'pill':
            default:
                return html `
					<div class="integration-account integration-account--pill integration-account--loading">
						<uo-placeholder width="24px" height="24px"></uo-placeholder>
						<uo-placeholder width="100px"></uo-placeholder>
						${this.message ? html `
							<uo-placeholder width="16px" height="16px"></uo-placeholder>
						` : ''}
						<uo-placeholder width="16px" height="16px"></uo-placeholder>
					</div>
				`;
        }
    }
    /**
     * Render the account info based on variant
     *
     * @return {TemplateResult} The rendered account info template
     */
    renderAccountInfo() {
        if (this.loading) {
            return this.renderLoadingState();
        }
        const classes = `integration-account integration-account--${this.variant} ${this.class}`.trim();
        switch (this.variant) {
            case 'panel':
                return html `
					<div class="${classes}">
						<div class="integration-account__avatar">
							${this.renderAvatar(48)}
						</div>
						<div class="integration-account__content">
							${this.renderUsername(true)}
							${this.renderMessage()}
							<slot></slot>
						</div>
					</div>
				`;
            case 'badge':
                return html `
					<div class="${classes}" ?data-status="${this.status}">
						${this.username}
						${this.renderMessage()}
					</div>
				`;
            case 'pill':
            default:
                return html `
					<div class="${classes}">
						${this.renderAvatar()}
						${this.renderUsername()}
						${this.renderMessage()}
						${this.integration ? html `
							<uo-icon integration="${this.integration}" size="small"></uo-icon>
							` : ''}
					</div>
				`;
        }
    }
    /**
     * Render the component
     *
     * @return {TemplateResult} The rendered component template
     */
    render() {
        return this.renderAccountInfo();
    }
};
_AutomatorIntegrationAccount_username_accessor_storage = new WeakMap();
_AutomatorIntegrationAccount_avatar_accessor_storage = new WeakMap();
_AutomatorIntegrationAccount_integration_accessor_storage = new WeakMap();
_AutomatorIntegrationAccount_variant_accessor_storage = new WeakMap();
_AutomatorIntegrationAccount_status_accessor_storage = new WeakMap();
_AutomatorIntegrationAccount_loading_accessor_storage = new WeakMap();
_AutomatorIntegrationAccount_message_accessor_storage = new WeakMap();
_AutomatorIntegrationAccount_class_accessor_storage = new WeakMap();
AutomatorIntegrationAccount.styles = css `${unsafeCSS(styles)}`;
__decorate([
    property({ type: String })
], AutomatorIntegrationAccount.prototype, "username", null);
__decorate([
    property({ type: String })
], AutomatorIntegrationAccount.prototype, "avatar", null);
__decorate([
    property({ type: String })
], AutomatorIntegrationAccount.prototype, "integration", null);
__decorate([
    property({ type: String })
], AutomatorIntegrationAccount.prototype, "variant", null);
__decorate([
    property({ type: String })
], AutomatorIntegrationAccount.prototype, "status", null);
__decorate([
    property({ type: Boolean })
], AutomatorIntegrationAccount.prototype, "loading", null);
__decorate([
    property({ type: String })
], AutomatorIntegrationAccount.prototype, "message", null);
__decorate([
    property({ type: String })
], AutomatorIntegrationAccount.prototype, "class", null);
AutomatorIntegrationAccount = __decorate([
    customElement('uo-integration-account')
], AutomatorIntegrationAccount);
export { AutomatorIntegrationAccount };
